import { Fragment } from 'react';
import type { ApiEndpoints } from '@avisoma/shared';
import { FaFile } from 'react-icons/fa6';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { PeekLink } from '../../components/PeekLink';
import { ID } from '../../components/ID';

interface UtilisationSummaryProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

/**
 * Why no utilisation value could be published (DR-3767). An empty summary is a deliberate
 * withdrawal — the project holds utilisation documents, none of them belongs to the
 * engine's aircraft — so the card states the reason instead of disappearing.
 */
const UNAVAILABLE_REASONS: Record<string, string> = {
  'no-usable-source': 'No usable utilisation reading in the documents',
  'no-matching-aircraft': "No utilisation source matches the engine's aircraft",
  'aircraft-unknown': "Engine's aircraft unknown",
  'engine-detached': 'Engine off-wing',
};

export function UtilisationSummary({ response }: UtilisationSummaryProps) {
  const utilisation = response.item.data?.utilisation;

  if (!utilisation) {
    return null;
  }

  const unavailable = utilisation.unavailableReason
    ? (UNAVAILABLE_REASONS[utilisation.unavailableReason] ?? 'No utilisation source available')
    : null;

  const fields = (
    [
      ['Last Flight Date', utilisation.lastFlightDate],
      ['Report Date', utilisation.reportDate],
      ['Total Air Hours (TAH)', utilisation.latestTAH],
      ['Total Air Cycles (TAC)', utilisation.latestTAC],
      ['Aircraft Registration', utilisation.aircraftRegistration],
      ['Aircraft MSN', utilisation.aircraftMsn],
      ['Source', utilisation.sourceCategory],
    ] as [string, string | number | null | undefined][]
  ).filter((entry): entry is [string, string | number] => entry[1] != null);

  return (
    <Card
      header={
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Utilisation</h2>
        </div>
      }
    >
      {unavailable ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">{unavailable}</p>
      ) : (
        <DescriptionList>
          {fields.map(([label, value]) => (
            <Fragment key={label}>
              <DescriptionTerm>{label}</DescriptionTerm>
              <DescriptionDetails>{String(value)}</DescriptionDetails>
            </Fragment>
          ))}
          {utilisation.sourcePageId && (
            <>
              <DescriptionTerm>Source Page</DescriptionTerm>
              <DescriptionDetails>
                <PeekLink
                  type="page"
                  id={utilisation.sourcePageId}
                  className="inline-flex items-center gap-1"
                >
                  <FaFile className="size-3" />
                  <ID id={utilisation.sourcePageId} />
                </PeekLink>
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </Card>
  );
}
